import React from 'react';

import { Box } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      display: 'flex',
      gridGap: 8,
      flexWrap: 'wrap',
    },
    pill: {
      cursor: 'pointer',
      padding: `${theme.spacing(0.75)}px ${theme.spacing(2)}px`,
      borderRadius: 999,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.4,
      transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
      userSelect: 'none',
      whiteSpace: 'nowrap',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
      },
    },
    pillActive: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.getContrastText(theme.palette.primary.main),
      borderColor: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        color: theme.palette.getContrastText(theme.palette.primary.dark),
      },
    },
  })
);

export interface FilterPillOption<T extends string = string> {
  value: T;
  label: string;
}

interface Props<T extends string> {
  options: FilterPillOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
  /** data-cy is rendered as `${cyPrefix}-${option.value}` when set */
  cyPrefix?: string;
}

/** Single-select pill row used as a lightweight inline filter (tab-like semantics). */
export const FilterPills = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  cyPrefix,
}: Props<T>) => {
  const classes = useStyles();

  return (
    <Box className={clsx(classes.row, className)} role="tablist" aria-label={ariaLabel}>
      {options.map(option => {
        const isActive = option.value === value;
        return (
          <Box
            key={option.value}
            role="tab"
            aria-selected={isActive}
            tabIndex={0}
            className={clsx(classes.pill, { [classes.pillActive]: isActive })}
            onClick={() => onChange(option.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onChange(option.value);
              }
            }}
            data-cy={cyPrefix ? `${cyPrefix}-${option.value}` : undefined}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
};
